import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Loading from '../../components/Loading';
import { fetchClassDecks, isDue, type Card, type ReviewDay } from '../../lib/cards';
import { dayKey, dayKeyOffset, startOfDay } from '../../lib/dayBoundary';
import { shortDate } from '../../lib/dates';
import { useClassroom } from '../../state/Classroom';

// ─────────────────────────────────────────────────────────────────────────────
// /students/:id — one student's vocabulary, for the teacher.
//
// Every card they saved, and how much they have studied it: times reviewed,
// times forgotten, when last reviewed, when it is next due, and how settled it
// is (the scheduling interval — a card on a 30-day interval is one they know).
// Above it, their review habit over the last two weeks.
// Read-only: RLS lets a teacher SELECT their students' cards, never change them.
// ─────────────────────────────────────────────────────────────────────────────

type Sort = 'recent' | 'az' | 'forgot' | 'reviews';

/** How well a card is known, from its interval: what the schedule believes. */
function strength(c: Card): { label: string; cls: string } {
  if (c.reviews === 0) return { label: 'Not studied', cls: 'todo' };
  if (c.review_count === 0) return { label: 'Learning', cls: 'weak' };
  if (c.interval < 7) return { label: 'Getting there', cls: 'started' };
  if (c.interval < 21) return { label: 'Solid', cls: 'done' };
  return { label: 'Known', cls: 'known' };
}

export default function StudentVocab() {
  const { id } = useParams();
  const { hydrated, students } = useClassroom();
  const student = students.find(s => s.user_id === id);
  const [deck, setDeck] = useState<{ cards: Card[]; days: ReviewDay[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sort, setSort] = useState<Sort>('recent');
  const [q, setQ] = useState('');

  useEffect(() => {
    if (!id) return;
    fetchClassDecks([id]).then(setDeck).catch(e => setError(e instanceof Error ? e.message : String(e)));
  }, [id]);

  const cards = useMemo(() => {
    const list = deck?.cards ?? [];
    const needle = q.trim().toLowerCase();
    const filtered = needle
      ? list.filter(c => c.term.toLowerCase().includes(needle) || c.translation.toLowerCase().includes(needle))
      : list;
    const by: Record<Sort, (a: Card, b: Card) => number> = {
      recent: (a, b) => b.created_at.localeCompare(a.created_at),
      az: (a, b) => a.term.localeCompare(b.term, a.source_lang || 'es', { sensitivity: 'base' }),
      forgot: (a, b) => b.lapses - a.lapses || b.reviews - a.reviews,
      reviews: (a, b) => b.reviews - a.reviews,
    };
    return [...filtered].sort(by[sort]);
  }, [deck, q, sort]);

  if (!hydrated || (!deck && !error)) return <Loading what="their vocabulary" />;
  if (!student) {
    return <div className="notice">That student isn't in this class. <Link to="/roster">Students</Link></div>;
  }
  if (error) return <div className="notice bad">{error}</div>;

  const all = deck!.cards;
  const days = deck!.days;
  const fortnight = Array.from({ length: 14 }, (_, i) => dayKeyOffset(13 - i));
  const byDay = new Map(days.map(d => [d.day, d]));
  const daysReviewed = fortnight.filter(d => (byDay.get(d)?.reviewed ?? 0) > 0).length;
  const totalReviews = all.reduce((n, c) => n + c.reviews, 0);
  const totalForgot = all.reduce((n, c) => n + c.lapses, 0);
  const remembered = totalReviews > 0 ? Math.round(((totalReviews - totalForgot) / totalReviews) * 100) : null;
  const due = all.filter(c => isDue(c)).length;
  const dayStart = startOfDay().getTime();
  const reviewedToday = (byDay.get(dayKey())?.reviewed ?? 0) > 0
    || all.some(c => c.last_reviewed_at && new Date(c.last_reviewed_at).getTime() >= dayStart);

  return (
    <div>
      <div className="crumbs"><Link to="/roster">← Students</Link></div>
      <h1>{student.display_name}</h1>
      <p className="sub">{student.username} · vocabulary and flashcard study</p>

      <section className="stats">
        <div className="stat"><span className="n">{all.length}</span><span className="k">Words saved</span></div>
        <div className={`stat${due > 0 ? ' warn' : ''}`}><span className="n">{due}</span><span className="k">Due now{reviewedToday ? ' · reviewed today' : ''}</span></div>
        <div className="stat"><span className="n">{daysReviewed}/14</span><span className="k">Days studied, last 2 weeks</span></div>
        <div className="stat"><span className="n">{remembered === null ? '—' : `${remembered}%`}</span><span className="k">Remembered ({totalReviews} reviews)</span></div>
      </section>

      <div className="panel" style={{ marginTop: 14 }}>
        <div className="panel-head"><h3>Review habit</h3><span className="small muted">last 14 days, today on the right</span></div>
        <div className="fortnight">
          {fortnight.map(d => {
            const r = byDay.get(d);
            const n = r?.reviewed ?? 0;
            return (
              <div key={d} className="fn-day" title={`${shortDate(d)}: ${n} reviewed${r?.forgot ? `, ${r.forgot} forgotten` : ''}`}>
                <div className="fn-bar"><i style={{ height: `${Math.min(100, n * 5)}%` }} className={n > 0 ? 'on' : ''} /></div>
                <span className={`fn-label${d === dayKey() ? ' today' : ''}`}>{new Date(`${d}T12:00`).toLocaleDateString(undefined, { weekday: 'narrow' })}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="row" style={{ margin: '24px 0 10px' }}>
        <h2 style={{ margin: 0 }}>Words</h2>
        <span className="spacer" />
        <select style={{ width: 'auto' }} value={sort} onChange={e => setSort(e.target.value as Sort)}>
          <option value="recent">Newest first</option>
          <option value="az">A–Z</option>
          <option value="forgot">Most forgotten</option>
          <option value="reviews">Most reviewed</option>
        </select>
        <input style={{ maxWidth: 200 }} placeholder="Search…" value={q} onChange={e => setQ(e.target.value)} />
      </div>

      {all.length === 0
        ? <div className="notice">{student.display_name} hasn't saved any words yet.</div>
        : (
          <div className="table-wrap">
            <table className="grid vocab-table">
              <thead>
                <tr>
                  <th>Word or phrase</th>
                  <th>Studied</th>
                  <th>Forgot</th>
                  <th>Last review</th>
                  <th>Next due</th>
                  <th>How well</th>
                </tr>
              </thead>
              <tbody>
                {cards.map(c => {
                  const st = strength(c);
                  return (
                    <tr key={c.id}>
                      <td>
                        <div className="term">{c.term}</div>
                        <div className="small muted">{c.translation}</div>
                      </td>
                      <td className="mono">{c.reviews}×</td>
                      <td className="mono">{c.lapses > 0 ? `${c.lapses}×` : '—'}</td>
                      <td className="small">{c.last_reviewed_at ? shortDate(c.last_reviewed_at) : <span className="muted">Never</span>}</td>
                      <td className="small">{isDue(c) ? <span className="due-now">Due now</span> : shortDate(c.next_review)}</td>
                      <td><span className={`status ${st.cls}`}>{st.label}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}
